import filesize from 'file-size';
import type { MomentInput } from 'moment';
import moment from 'moment';
import 'moment-timezone';
import { ceil, floor, isEmpty, round } from 'lodash-es';
import type { OptionsWithTZ } from 'date-fns-tz';
import { formatInTimeZone } from 'date-fns-tz';
import { getBrowserTimezone } from './get-browser-timezone';
import { type Profile } from '../profile';
import { schema } from '../schema';
import type {
    FormField,
    SchemaFloatField,
    SchemaIntegerField,
    TranslatedFormField,
} from '../components/pages/measuringPoint/types';
import { hasUnitConversion } from '../components/pages/measuringPoint/types';

interface FormatOptions {
    timezone: string | null;
    useBrowserTimezone: boolean;
    hideDate: boolean;
    hideTime: boolean;
    hideSeconds: boolean;
    dateFormat: string;
}

// TIME_FORMAT is set from the base_includes.html template

const defaultFormatOptions = {
    timezone: null,
    useBrowserTimezone: false,
    hideDate: false,
    hideTime: false,
    hideSeconds: false,
    dateFormat: 'dd-MM-yyyy',
};

// This method adjusts the time format tokens from moment.js to those used by date-fns.
// The original `TIME_FORMAT` used tokens from moment.js, which were based on
// the unstandardized PHP format. The date-fns library’s tokens are influenced
// by the Unicode Technical Standard #35, specifically the Unicode Locale Data
// Markup Language (LDML). As we transition from the deprecated moment.js towards
// date-fns, we currently need to maintain compatibility with both. Here, we adapt
// the tokens: `A` (AM/PM in moment.js) is replaced with `aa` (AM/PM in date-fns)
// as specified by LDML.
function adjustTimeFormatForCompatibility(timeFormat: string) {
    // Replace moment.js 'A' token with date-fns 'aa' for AM/PM.
    return timeFormat.replace('A', 'aa');
}

export function format(
    date: Parameters<typeof formatInTimeZone>[0],
    optionsWithoutDefaults: Partial<FormatOptions>
) {
    const options = {
        ...defaultFormatOptions,
        ...optionsWithoutDefaults,
    };

    if (!options.timezone && !options.useBrowserTimezone) {
        throw Error('No timezone provided.');
    }

    const timezone = options.timezone ?? getBrowserTimezone();

    const formattedParts = [];

    if (!options.hideDate) {
        formattedParts.push(formatInTimeZone(date, timezone, options.dateFormat));
    }

    if (!options.hideTime) {
        // Adjust time format for compatibility between moment.js and date-fns.
        const adjustedTimeFormat = adjustTimeFormatForCompatibility(TIME_FORMAT);

        // Optionally remove seconds component.
        const timeFormat = options.hideSeconds
            ? adjustedTimeFormat.replace(':ss', '')
            : adjustedTimeFormat;

        formattedParts.push(formatInTimeZone(date, timezone, timeFormat));
    }

    return formattedParts.join(' ');
}

export function formatInUtc(date: Date, formatStr: string, options: OptionsWithTZ) {
    return formatInTimeZone(date, 'UTC', formatStr, options);
}

/**
 * Formats the time to either 24h format or 12h format.
 * Format is decided by the user profile settings
 *
 * @param {string} timezone
 * @param {*} value MomentInput
 * @returns {string}
 */
export function formatTimeTz(timezone: string, value: MomentInput) {
    return moment(value).tz(timezone).format(TIME_FORMAT);
}

export function formatTime(value: MomentInput) {
    return formatTimeTz(getBrowserTimezone(), value);
}

export function formatDateTz(timezone: string, value: MomentInput) {
    return moment(value).tz(timezone).format('DD-MM-YYYY');
}

/**
 * Formats the given time to either 24h or 12h with date as well.
 * Format is decided by the user profile settings.
 *
 * @param {string} timezone
 * @param {*} value MomentInput
 * @returns {string}
 */
export function formatDatetimeTz(timezone: string, value: MomentInput) {
    return `${formatDateTz(timezone, value)} ${formatTimeTz(timezone, value)}`;
}

export function formatDatetime(value: MomentInput) {
    return formatDatetimeTz(getBrowserTimezone(), value);
}

function getDistanceUnitMultiplier(distanceUnit: DistanceUnit) {
    const multiplier = schema.distanceUnitMultipliers[distanceUnit];

    if (!multiplier) {
        throw new Error(`Could not find distance unit multiplier for unit ${distanceUnit}.`);
    }

    return multiplier;
}

/**
 * Expects a value in mm.
 * Will return the value as expected by the user profile.
 *
 * @param {number} distanceInMm
 * @returns {number}
 */
export function distanceConverter(distanceInMm: number, distanceUnit: DistanceUnit) {
    return distanceInMm / getDistanceUnitMultiplier(distanceUnit);
}

export function revertDistanceConverter(distanceInMm: number, distanceUnit: DistanceUnit) {
    // All values are multiplied in that function so we can
    // divide them to get the original value (more or less).
    return distanceInMm * getDistanceUnitMultiplier(distanceUnit);
}

export function replaceDistanceUnit(string: string | null, distanceUnit: DistanceUnit): string {
    if (string === null) {
        return '';
    }
    return string.replace(/mm/g, schema.distanceUnitLabels[distanceUnit]);
}

export function roundToN(num: number, n = 2) {
    return round(num, n);
}

export function getDigitsForUnit(unit: DistanceUnit) {
    return schema.distanceUnitDigits[unit];
}

export function getDigitsForField(fieldName: string, distanceUnit: DistanceUnit) {
    // Adds an extra digit for all fields. For the VTOP 'flatLevel' field,
    // it adds 3 extra digit to prevent truncation of its very
    // small minimum value (0.000787401575) and also the case when it
    // gets divide by 100 (in the `NotificationConfigField`).
    const baseDigits = getDigitsForUnit(distanceUnit);

    if (fieldName === 'flatLevel') {
        return baseDigits + 3;
    }

    return baseDigits + 1;
}

export function roundToDistanceUnitDigits(
    num: number,
    fieldName: string,
    distanceUnit: DistanceUnit
) {
    return round(num, getDigitsForField(fieldName, distanceUnit));
}

export function roundToDistanceUnitDigitsUp(
    num: number,
    fieldName: string,
    distanceUnit: DistanceUnit
) {
    return ceil(num, getDigitsForField(fieldName, distanceUnit));
}

export function roundToDistanceUnitDigitsDown(
    num: number,
    fieldName: string,
    distanceUnit: DistanceUnit
) {
    return floor(num, getDigitsForField(fieldName, distanceUnit));
}

interface DisplayRoundedField {
    name: string;
    unitConversion?: string;
    // Exact bounds, expressed in the same (already converted) unit as the value being rounded.
    minValue?: number;
    maxValue?: number;
}

function roundConvertedDistance<T>(
    field: DisplayRoundedField,
    value: T,
    distanceUnit: DistanceUnit,
    rounder: (num: number, fieldName: string, distanceUnit: DistanceUnit) => number
): T {
    if (field.unitConversion !== 'mm_to_user' || typeof value !== 'number') {
        return value;
    }
    return rounder(value, field.name, distanceUnit) as T;
}

export function roundConvertedMinValueForDisplay<T>(
    field: DisplayRoundedField,
    value: T,
    distanceUnit: DistanceUnit
): T {
    return roundConvertedDistance(field, value, distanceUnit, roundToDistanceUnitDigitsUp);
}

export function roundConvertedMaxValueForDisplay<T>(
    field: DisplayRoundedField,
    value: T,
    distanceUnit: DistanceUnit
): T {
    return roundConvertedDistance(field, value, distanceUnit, roundToDistanceUnitDigitsDown);
}

/**
 * Rounds a converted `mm_to_user` value for display without moving it across an exact
 * validation bound: values on a bound snap to the displayed bound, values outside the
 * bounds are left untouched. Bounds must use the same converted unit as `value`.
 */
export function roundConvertedDistanceValueForDisplay<T>(
    field: DisplayRoundedField,
    value: T,
    distanceUnit: DistanceUnit
): T {
    const rounded = roundConvertedDistance(field, value, distanceUnit, roundToDistanceUnitDigits);

    if (typeof value !== 'number' || typeof rounded !== 'number') {
        return rounded;
    }

    const { minValue, maxValue } = field;

    if (typeof minValue === 'number' && value < minValue && rounded >= minValue) {
        return value;
    }

    if (typeof minValue === 'number' && value >= minValue && rounded < minValue) {
        return roundConvertedMinValueForDisplay(field, minValue, distanceUnit) as T;
    }

    if (typeof maxValue === 'number' && value > maxValue && rounded <= maxValue) {
        return value;
    }

    if (typeof maxValue === 'number' && value <= maxValue && rounded > maxValue) {
        return roundConvertedMaxValueForDisplay(field, maxValue, distanceUnit) as T;
    }

    return rounded;
}

/**
 * Replaces the numeric min/max values (originally in mm/s) found in the given `helpText`
 * (e.g., 'At least 0.02 mm/s and at most 100 mm/s') with pre-converted values
 * based on the user's selected distance unit, as stored in the field's `minValue` and `maxValue`.
 * The function targets only standalone numbers to avoid replacing numbers within
 * words or units (e.g., 's^2').
 *
 * Example:
 *   helpText: 'At least 0.1 mm/s and at most 50 mm/s'
 *   field: { minValue: 4, maxValue: 200 }
 *   result: 'At least 4 mm/s and at most 200 mm/s'
 *
 * @param {string} helpText - The helpText text containing min/max values.
 * @param {object} field - The field object with minValue and/or maxValue.
 * @returns {string} Updated helpText with replaced values.
 */
export function replaceHelpTextMinMaxValues(
    helpText: string,
    field: Partial<SchemaIntegerField | SchemaFloatField> & { name: string },
    distanceUnit: DistanceUnit
): string {
    if (!helpText || !field) {
        return helpText;
    }

    const replacements: Array<number> = [];
    const minValue = 'minValue' in field ? field.minValue : undefined;
    if (minValue !== undefined) {
        replacements.push(roundToDistanceUnitDigitsUp(minValue, field.name, distanceUnit));
    }
    const maxValue = 'maxValue' in field ? field.maxValue : undefined;
    if (maxValue !== undefined) {
        replacements.push(roundToDistanceUnitDigitsDown(maxValue, field.name, distanceUnit));
    }

    if (isEmpty(replacements)) {
        return helpText;
    }

    let replacementIndex = 0;

    // The regex matches standalone numbers at the start, middle, or end of the string.
    const regex = /(?<!\S)([+-]?\d*\.?\d+)(?!\S)/g;

    return helpText.replace(regex, () => {
        const replacementValue = replacements[replacementIndex];
        replacementIndex += 1;
        return String(replacementValue);
    });
}

export function formatFileSize(bytes: number | null) {
    return bytes !== null ? filesize(bytes, { fixed: 0 }).human('si') : '';
}

export function convertUnits(
    field: FormField | TranslatedFormField,
    value: number,
    profile: Profile,
    reverse?: boolean
): number;
export function convertUnits(
    field: FormField | TranslatedFormField,
    value: null | undefined,
    profile: Profile,
    reverse?: boolean
): null | undefined;
export function convertUnits(
    field: FormField | TranslatedFormField,
    value: unknown,
    profile: Profile,
    reverse?: boolean
): unknown;
export function convertUnits(
    field: FormField | TranslatedFormField,
    value: unknown,
    profile: Profile,
    reverse = false
) {
    if (
        !hasUnitConversion(field) ||
        value === null ||
        value === undefined ||
        typeof value !== 'number' ||
        !Number.isFinite(value)
    ) {
        return value;
    }

    const { unitConversion } = field;

    switch (unitConversion) {
        case 'ms_to_sec':
            return reverse ? value * 1000 : value / 1000;
        case 'mm_to_user':
            return reverse
                ? revertDistanceConverter(value, profile.distanceUnit)
                : distanceConverter(value, profile.distanceUnit);
        default:
            return value;
    }
}

export function convertUnitsForDisplay(
    field: FormField | TranslatedFormField,
    value: number,
    profile: Profile
): number;
export function convertUnitsForDisplay(
    field: FormField | TranslatedFormField,
    value: null | undefined,
    profile: Profile
): null | undefined;
export function convertUnitsForDisplay(
    field: FormField | TranslatedFormField,
    value: unknown,
    profile: Profile
): unknown;
export function convertUnitsForDisplay(
    field: FormField | TranslatedFormField,
    value: unknown,
    profile: Profile
) {
    const converted = convertUnits(field, value, profile);
    return roundConvertedDistanceValueForDisplay(field, converted, profile.distanceUnit);
}
